import abstractModel from '../../core/MercatelyModel';
import { MERCATELY_API } from '../../constants/AppConstants';

const resource = `${MERCATELY_API}/pricing`;

const model = abstractModel(resource);

const getCSRFToken = () => (
  document.querySelector('meta[name="csrf-token"]')?.content
    || document.querySelector('[name="csrf-token"]')?.content
);

/**
 * GET /api/v1/pricing
 * Returns { pricing: { payment_plan, partner_config, retailer, payment_methods, stripe_transactions, paymentez_transactions, paymentez_sdk_config, stripe_sdk_config } }
 */
model.getPricing = async () => {
  const res = await model.get({}, { path: resource });
  return res?.pricing ?? res;
};

/**
 * GET /api/v1/consumption
 * Returns payment_plan (minimal), consumption_rows, interactions, gupshup_integrated, whatsapp_integrated
 */
model.getConsumption = async () => model.get({}, { path: `${resource.replace('/pricing', '')}/consumption` });

/**
 * GET /api/v1/invoice/:id
 * Returns transaction, bill_details or billed_to, reference_code, locale
 * (company_info_text and amount_in_words are computed in the client from locale and transaction_type)
 * @param {string} id - transaction web_id
 */
model.getInvoice = async (id) => {
  const base = resource.replace('/pricing', '');
  return model.get({}, { path: `${base}/invoice/${id}` });
};

/**
 * POST /retailers/:slug/charge
 * Responds with JSON when Accept: application/json
 * @param {string} slug - retailer slug (e.g. ENV.SLUG or useParams().slug)
 * @returns {Promise<{ success: boolean, message: string }>}
 */
model.charge = async (slug) => {
  const path = `/retailers/${slug}/charge`;
  return model.post({}, { path, csrfToken: getCSRFToken() });
};

/**
 * POST /api/v1/plan_cancellations/desist_cancellation
 * @returns {Promise<{ success: boolean, message: string }>}
 */
model.desistCancellation = async () => {
  const path = `${MERCATELY_API}/plan_cancellations/desist_cancellation`;
  return model.post({}, { path, csrfToken: getCSRFToken() });
};

const paymentMethodsResource = `${MERCATELY_API}/payment_methods`;

/**
 * PUT /api/v1/payment_methods/set_default/:id
 * @param {string} id - stripe_pm_id (Stripe) or card id (Paymentez)
 */
model.setDefaultPaymentMethod = async (id) => {
  const path = `${paymentMethodsResource}/set_default/${id}`;
  return model.put({}, { path, csrfToken: getCSRFToken() });
};

/**
 * DELETE /api/v1/payment_methods/:id
 * @param {string} id - stripe_pm_id (Stripe) or card id (Paymentez)
 */
model.deletePaymentMethod = async (id) => model.remove(
  { id },
  { path: paymentMethodsResource, csrfToken: getCSRFToken() }
);

/**
 * POST /api/v1/plan_cancellations (cancel plan)
 * @param {Object} params - { plan_cancellation: { reason, comment, migrating_to, inactivity_period, improvements_wanted, free_text } }
 */
model.cancelPlan = async (params) => {
  const path = `${MERCATELY_API}/plan_cancellations`;
  return model.post(params, { path, csrfToken: getCSRFToken() });
};

/**
 * POST /retailers/:slug/stripe/add_balance or /retailers/:slug/paymentez/add_balance
 * @param {string} slug - retailer slug
 * @param {Object} payload - { amount: number, cc_id: string }
 * @param {boolean} usePaymentez - true for Paymentez, false for Stripe
 * @returns {Promise<{ message: string }>}
 */
model.addBalance = async (slug, payload, usePaymentez) => {
  const path = usePaymentez
    ? `/retailers/${slug}/paymentez/add_balance`
    : `/retailers/${slug}/stripe/add_balance`;
  const body = { card: { amount: Number(payload.amount), cc_id: payload.cc_id } };
  return model.post(body, { path, csrfToken: getCSRFToken() });
};

/**
 * POST /retailers/:slug/payment_methods/create-setup-intent (Stripe)
 * @param {string} slug - retailer slug
 * @returns {Promise<{ data: { client_secret: string } }>}
 */
model.createSetupIntent = async (slug) => {
  const path = `/retailers/${slug}/payment_methods/create-setup-intent`;
  return model.post({}, { path, csrfToken: getCSRFToken() });
};

/**
 * POST /retailers/:slug/payment_methods (save Stripe payment method after confirmCardSetup)
 * @param {string} slug - retailer slug
 * @param {Object} payload - { payment_method: string }
 * @returns {Promise<{ stripe_pm_id: string, main: boolean, ... }>}
 */
model.createPaymentMethod = async (slug, payload) => {
  const path = `/retailers/${slug}/payment_methods`;
  return model.post(payload, { path, csrfToken: getCSRFToken() });
};

/**
 * POST /retailers/:slug/paymentez (save Paymentez card after Payment.addCard)
 * @param {string} slug - retailer slug
 * @param {Object} payload - { cardToken: object, holder_name: string }
 * @returns {Promise<{ notice: string }>}
 */
model.addPaymentezCard = async (slug, payload) => {
  const path = `/retailers/${slug}/paymentez`;
  return model.post(payload, { path, csrfToken: getCSRFToken() });
};

/**
 * POST /api/v1/acquired_addons/:id/cancel_addon
 * @param {string} id - AcquiredAddon id (from serializer)
 */
model.cancelAddonDelayed = async (id) => {
  const path = `${MERCATELY_API}/acquired_addons/${id}/cancel_addon`;
  return model.post({}, { path, csrfToken: getCSRFToken() });
};

/**
 * POST /api/v1/acquired_addons/:id/desist_cancellation
 * @param {string} id - AcquiredAddon id (from serializer)
 */
model.desistAddonCancellation = async (id) => {
  const path = `${MERCATELY_API}/acquired_addons/${id}/desist_cancellation`;
  return model.post({}, { path, csrfToken: getCSRFToken() });
};

export default model;
